import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { submitCopyrightRequest } from '../services/copyrightApi';
import styles from './CopyrightPage.module.css';

const emptyForm = {
  name: '',
  email: '',
  organization: '',
  targetUrl: '',
  darknewsUrl: '',
  notes: '',
  goodFaith: false,
  website: '',
};

const CopyrightPage = () => {
  usePageMeta({
    title: 'Copyright / DMCA',
    path: '/copyright',
    description: 'Submit a copyright or feed-removal request. DarkNews acknowledges within 48 hours.',
  });
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [receiptId, setReceiptId] = useState(null);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitCopyrightRequest(form);
      setReceiptId(result?.id || 'received');
      setForm(emptyForm);
    } catch (err) {
      setError(err.message || 'Unable to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`more-page ${styles.page}`}>
      <div className="container">
        <div className={`more-page-header ${styles.pageHeader}`}>
          <h1>Copyright / DMCA</h1>
          <p className={styles.subtitle}>
            We honor valid notices. If you do not want DarkNews to harvest your feed, just ask.
          </p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>Disclaimer</h2>
            <p>
              DarkNews is a news aggregation and research platform. We harvest publicly available RSS
              feeds, show headlines and RSS descriptions, and link out to the original publisher. We
              do not reprint full articles. Scraped text, when stored, is used for analytics only
              (quality scoring, embeddings, entity extraction) — not as a substitute for the
              publisher&apos;s page.
            </p>
            <p>
              If you are a rights holder or publisher and want a feed removed from our harvest list,
              submit the form below. We are happy to disable it. We aim to acknowledge requests
              within 48 hours and act within 5 business days.
            </p>
            <p>
              This page is our designated intake for copyright and feed-removal notices under the
              Digital Millennium Copyright Act (17 U.S.C. § 512). You may also write{' '}
              <a href="mailto:copyright@darknews.ai">copyright@darknews.ai</a>. See{' '}
              <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy</Link>.
            </p>
          </section>

          {receiptId ? (
            <section className={styles.section}>
              <h2>Request received</h2>
              <p>
                Thank you. We logged your request
                {receiptId !== 'received' ? ` (${receiptId})` : ''}. We will review it and disable
                matching RSS feeds when we confirm the site. Acknowledge within 48 hours; action
                within 5 business days.
              </p>
              <button type="button" className={styles.secondaryButton} onClick={() => setReceiptId(null)}>
                Submit another request
              </button>
            </section>
          ) : (
            <section className={styles.section}>
              <h2>Ask us to remove a feed</h2>
              <form onSubmit={onSubmit} className={styles.form}>
                <div className={styles.honeypot} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>
                    Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    className={styles.input}
                    value={form.name}
                    onChange={onChange}
                    required
                    maxLength={200}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={styles.input}
                    value={form.email}
                    onChange={onChange}
                    required
                    maxLength={320}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="organization" className={styles.label}>
                    Organization
                  </label>
                  <input
                    id="organization"
                    name="organization"
                    className={styles.input}
                    value={form.organization}
                    onChange={onChange}
                    maxLength={300}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="targetUrl" className={styles.label}>
                    Site or RSS feed URL to remove *
                  </label>
                  <input
                    type="url"
                    id="targetUrl"
                    name="targetUrl"
                    className={styles.input}
                    value={form.targetUrl}
                    onChange={onChange}
                    required
                    placeholder="https://example.com/feed"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="darknewsUrl" className={styles.label}>
                    DarkNews article URL (optional)
                  </label>
                  <input
                    type="url"
                    id="darknewsUrl"
                    name="darknewsUrl"
                    className={styles.input}
                    value={form.darknewsUrl}
                    onChange={onChange}
                    placeholder="https://darknews.ai/article/…"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="notes" className={styles.label}>
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    className={styles.textarea}
                    value={form.notes}
                    onChange={onChange}
                    rows={5}
                    maxLength={4000}
                  />
                </div>

                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    name="goodFaith"
                    checked={form.goodFaith}
                    onChange={onChange}
                    required
                  />
                  I have a good-faith belief that I am authorized to request removal of this feed
                  or content from DarkNews.
                </label>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <button type="submit" className={styles.submitButton} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit request'}
                </button>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopyrightPage;
